import React, { useState, useEffect } from "react";
import Dropdown from "../../static/Dropdown/Dropdown";
import "./index.scss";
import {
  getMatchWinLeaderFilters,
  getMatchWinLeader
} from "../../services/tournaments";
import { dropdownItem } from "packages/static/Dropdown/types";
import MultiSelectDropdown from "../WhatsOnTable/MultiSelectDropdown";
import { dropdownItemSelected } from "../WhatsOnTable/types";

interface Filters {
  categories: dropdownItemSelected[];
  years: dropdownItem[];
  surfaces: dropdownItem[];
}

interface Player {
  totalLosses: number;
  totalWins: number;
  playerNationalityCode: string;
  fullName: string;
  givenName: string;
  familyName: string;
  playerProfileLink: string;
}

interface surfaces {
  name: string;
  code: string;
}

export default ({
  title,
  circuitCode
}: {
  title?: string;
  circuitCode: string;
}) => {
  const [matchData, setMatchData] = useState<Player[]>([]);
  const [isLoading, setLoadState] = useState({ value: true, error: false });
  const [bars, setBars] = useState<string[]>([]);
  const [filters, setFilters] = useState<Filters>({
    categories: [],
    years: [],
    surfaces: []
  });

  const { surfaces, years } = filters;

  const getSelected = (dd: dropdownItem[]) => {
    return dd.filter((d: dropdownItem) => d.selected)[0];
  };

  const sorted = (a: Player, b: Player) => {
    if (a.totalWins == b.totalWins) {
      return a.totalLosses - b.totalLosses;
    }
    return b.totalWins - a.totalWins;
  };

  const filtersNotEmpty = !!Object.keys(filters).filter(
    key => filters[key].length
  ).length;

  useEffect(() => {
    getMatchWinLeaderFilters({ circuitCode }).then(
      (filtersData: {
        categories: string[];
        surfaces: surfaces[];
        years: number[];
      }) => {
        const All = {
          selected: true,
          label: "All",
          value: ""
        };

        let categories = filtersData.categories
          .filter(value => value !== null)
          .map((value: string) => ({
            label: value,
            value: value,
            selected: false
          }));

        categories.splice(0, 0, All);

        let surfaces = filtersData.surfaces.map(
          (op: { code: string; name: string }) => ({
            label: op.name,
            value: op.code,
            selected: false
          })
        );

        surfaces.splice(0, 0, All);

        let years = filtersData.years.map((value: number, i: number) => ({
          label: `${value}`,
          value: value,
          selected: i === 0
        }));

        const minFilterOptions = 5;

        if (filtersData.years.length < minFilterOptions) {
          let offset: number = minFilterOptions - filtersData.years.length;
          let newYears = [];
          let earlist = filtersData.years.sort(
            (a: number, b: number) => a - b
          )[0];
          while (offset > 0) {
            newYears.push({
              value: earlist - offset,
              label: `${earlist - offset}`,
              selected: false
            });
            offset--;
          }
          years = [...years, ...newYears.reverse()];
        }

        setFilters({ categories, surfaces, years });
      }
    );
  }, []);

  useEffect(() => {
    if (filtersNotEmpty) {
      getMatchWinLeader({
        circuitCode,
        surfaceCode: getSelected(surfaces).value,
        category: categoriesString != "All" ? categoriesString : "",
        year: getSelected(years).value
      })
        .then((matchData: Player[]) => {
          setLoadState({ ...isLoading, value: false, error: false });
          setMatchData(matchData);

          if (matchData) {
            const first = matchData.sort(sorted)[0];
            const maxWidth = first.totalWins + first.totalLosses;

            let newBars = matchData.map(
              (player: Player) => `${(player.totalWins / maxWidth) * 100}%`
            );

            setBars(newBars);
          }
        })
        .catch(() => {
          setLoadState({
            value: false,
            error: true
          });
        });
    }
  }, [filters]);

  const handleOnChange = (key: string, target: { value: string }) => {
    setBars([]);
    setLoadState({ error: false, value: true });
    setFilters({
      ...filters,
      [key]: filters[key].map((cat: dropdownItem) => ({
        ...cat,
        selected: cat.value == target.value
      }))
    });
  };

  let categoriesString = filters.categories.reduce(
    (accumulator: string, currentValue: dropdownItemSelected) => {
      const selected = filters.categories.filter(cat => cat.selected);
      if (currentValue.selected) {
        return `${accumulator}${
          currentValue.label != selected[0].label ? ", " : ""
        }${currentValue.label}`;
      } else {
        return accumulator;
      }
    },
    ""
  );

  const changeCategory = (
    category: dropdownItemSelected,
    currentCategories: dropdownItemSelected[]
  ) => {
    const nextCategories: dropdownItemSelected[] = currentCategories.map(
      cat => {
        return {
          ...cat,
          selected: cat.value !== category.value ? cat.selected : !cat.selected
        };
      }
    );

    let selected = nextCategories.filter(cat => cat.selected);
    const flag = selected.some(cat => cat.label != "All");

    return nextCategories.map(cat => {
      if (category.label == "All") {
        return {
          ...cat,
          selected: cat.label == "All"
        };
      }
      if (cat.label == "All") {
        return {
          ...cat,
          selected: !flag
        };
      }
      return cat;
    });
  };

  return (
    <div className="matcheswon">
      <h2>{title ? title : "Match win leaders"}</h2>
      {filtersNotEmpty ? (
        <div className="matcheswon-filters">
          {filters.categories.length ? (
            <MultiSelectDropdown
              className={`matcheswon-filters__category`}
              value={categoriesString}
              optionOnClick={(target: dropdownItemSelected) => {
                setBars([]);
                setLoadState({ error: false, value: true });

                const nextCategories = changeCategory(
                  target,
                  filters.categories
                );
                setFilters({
                  ...filters,
                  categories: nextCategories
                });
              }}
              options={filters.categories}
            />
          ) : null}
          {Object.keys(filters)
            .filter(key => key !== "categories")
            .map(key =>
              filters[key].length ? (
                <Dropdown
                  key={key}
                  value={getSelected(filters[key])}
                  options={filters[key]}
                  onChange={handleOnChange.bind(null, key)}
                />
              ) : null
            )}
        </div>
      ) : null}
      <div className="matcheswon-icons-container">
        <ul className="matcheswon-icons">
          {surfaces
            .filter(
              s =>
                getSelected(surfaces).label == "All" ||
                s.value == getSelected(surfaces).value
            )
            .map(s => (
              <li key={s.value} className={`matcheswon-icon--${s.value}`} />
            ))}
        </ul>
      </div>
      {isLoading.value ? (
        <div className="loading loading--in" />
      ) : (
        <>
          <div className="matcheswon-container">
            {matchData.length ? (
              matchData
                .sort(sorted)
                .slice(0, 3)
                .map((player: Player, index: number) => (
                  <div key={index} className={`matcheswon-item`}>
                    <div
                      className={`matcheswon-item__nation itf-flags itf-flags--${player.playerNationalityCode}`}
                    ></div>
                    <div className={`matcheswon-item__values`}>
                      <div
                        style={{ width: `${bars[index] ? bars[index] : 0}` }}
                        className={`matcheswon-item__values-wins matcheswon-item__values-wins--${index}`}
                      >
                        <a
                          href={player.playerProfileLink}
                          className="matcheswon-item__player-name"
                        >
                          <span className="desktop">{player.fullName}</span>
                          <div className="mobile">
                            <span>{`${player.givenName.substr(0, 1)}. ${
                              player.familyName
                            }`}</span>
                          </div>
                        </a>
                        <span className="desktop">{`${player.totalWins} wins`}</span>
                        <span className="mobile">{`${player.totalWins} w`}</span>
                      </div>
                      <div className="matcheswon-item__values-loses">
                        <span className="mobile">{`${player.totalLosses} l`}</span>
                        <span className="desktop">{`${player.totalLosses} losses`}</span>
                      </div>
                    </div>
                  </div>
                ))
            ) : (
              <p>
                No results are available for your selection. Please try again.
              </p>
            )}
            {isLoading.error ? (
              <p>Something went wrong. Please try again.</p>
            ) : null}
          </div>
          {matchData.length ? (
            <div className="matcheswon-container__notice">*main draw only</div>
          ) : null}
        </>
      )}
    </div>
  );
};
